import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTRPC } from '@/trpc/react.ts'
import { getPreviousMonthCreatedTime } from '../search.ts'

export function useCustomerSendPreview({ customerId, enabled }: { customerId: number; enabled: boolean }) {
  const trpc = useTRPC()
  const [range, setRange] = useState(getPreviousMonthCreatedTime)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 })
  const [selectedAccounts, setSelectedAccounts] = useState<Record<number, number | undefined>>({})
  const [selectedBot, setSelectedBot] = useState<string>()
  const query = useQuery({
    ...trpc.bill.previewCustomerSend.queryOptions({ customerId, ...range, ...pagination }),
    enabled,
    refetchOnWindowFocus: false
  })
  const accounts = Object.fromEntries(
    (query.data?.organizations ?? []).map((org) => {
      const selected = selectedAccounts[org.id]
      if (org.accounts.some((account) => account.id === selected)) return [org.id, selected]
      if (org.accounts.length === 1) return [org.id, org.accounts[0].id]
      return [org.id, undefined]
    })
  )
  const bots = query.data?.bots ?? []
  let botId = selectedBot
  if (!bots.some((bot) => bot.id === botId)) botId = bots.length === 1 ? bots[0].id : undefined
  const canSend =
    !!query.data?.billCount &&
    !!botId &&
    !query.isFetching &&
    !query.isError &&
    query.data.organizations.every((org) => !!accounts[org.id])

  const changeRange = (next: typeof range) => {
    setRange(next)
    setPagination((previous) => ({ ...previous, current: 1 }))
    setSelectedAccounts({})
  }
  const changePage = (current: number, pageSize: number) => setPagination({ current, pageSize })
  const changeAccount = (organizationId: number, accountId: number) =>
    setSelectedAccounts((previous) => ({ ...previous, [organizationId]: accountId }))
  return {
    query,
    range,
    pagination,
    accounts,
    botId,
    canSend,
    changeRange,
    changePage,
    changeAccount,
    changeBot: setSelectedBot
  }
}
